// Pure text helpers shared by the period list rendering (CameraView) and the
// client-side period search (periodSearch): how machine-formatted sensory entries
// become the prose a viewer actually reads.

import type { SensoryLogResponse } from "../../lib/api";

// The SSM entity pipeline appends one machine-formatted record entry per period:
// "entities: E1=<tall; dark hair; ...>; E2=<distant>". Internal E-ids mean nothing
// to owners, so it renders as a People section and is hidden from the raw text.
export const ENTITY_ENTRY_PREFIX = "entities: ";

export type PersonRecord = { id: string; phrase: string; distant: boolean };

const composePersonPhrase = (fields: Record<string, string>): string => {
  const sex = fields.sex ?? "";
  const noun = ["m", "male", "man"].includes(sex)
    ? "man"
    : ["f", "female", "woman"].includes(sex)
      ? "woman"
      : "person";
  let phrase = fields.height ? `a ${fields.height} ${noun}` : `a ${noun}`;
  if (fields.hair) phrase += ` with ${fields.hair} hair`;
  const wear = [fields.top, fields.bottom].filter(Boolean).join(" and ");
  if (wear) phrase += `, wearing ${wear}`;
  if (fields.accessories) phrase += `, carrying ${fields.accessories.split("+").join(", ")}`;
  return phrase;
};

export const parsePersonRecords = (observations: SensoryLogResponse[]): PersonRecord[] => {
  const out: PersonRecord[] = [];
  for (const o of observations) {
    for (const d of o.descriptions) {
      if (!d.startsWith(ENTITY_ENTRY_PREFIX)) continue;
      for (const m of d.slice(ENTITY_ENTRY_PREFIX.length).matchAll(/([A-Za-z0-9]+)=<([^>]*)>/g)) {
        const id = m[1] ?? "";
        const body = (m[2] ?? "").trim();
        if (body === "distant") {
          out.push({
            id,
            phrase: "a person seen at a distance",
            distant: true,
          });
          continue;
        }
        // Record bits are "field value" pairs (server/api/ssm_v2._serialize_records).
        const fields: Record<string, string> = {};
        for (const bit of body.split(";")) {
          const t = bit.trim();
          const space = t.indexOf(" ");
          if (space <= 0) continue;
          const value = t.slice(space + 1).trim();
          if (value && value !== "unknown") fields[t.slice(0, space)] = value;
        }
        out.push({ id, phrase: composePersonPhrase(fields), distant: false });
      }
    }
  }
  return out;
};

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

export const peoplePills = (records: PersonRecord[]): string[] => {
  const pills = records.filter((r) => !r.distant).map((r) => capitalize(r.phrase));
  const distant = records.filter((r) => r.distant).length;
  if (distant === 1) pills.push("Seen at a distance");
  if (distant > 1) pills.push(`${distant} people seen at a distance`);
  return pills;
};

// Pre-2026-07-21 P4 descriptions reference internal entity ids (E1, E2). Substitute
// them at render time with the natural descriptor from the period's records — the
// demo-site approach (convert shorthand at the mention site), applied to history.
export const substituteEntityIds = (text: string, records: PersonRecord[]): string => {
  const byId = new Map(records.map((r) => [r.id, r.phrase]));
  const substituted = text.replace(/\bE(\d+)\b/g, (full, n: string) => byId.get(`E${n}`) ?? full);
  if (substituted === text) return text;
  // Re-capitalize sentence starts the lowercase phrases may have broken.
  return substituted.replace(
    /(^|[.!?]\s+)([a-z])/g,
    (_m, lead: string, ch: string) => lead + ch.toUpperCase(),
  );
};

// Exactly the text the Observations section renders for one sensory row.
export const observationDisplayText = (o: SensoryLogResponse): string =>
  o.descriptions.filter((d) => !d.startsWith(ENTITY_ENTRY_PREFIX)).join("; ");
